import React, { useCallback, useEffect } from "react";

interface IViewportContext {
  width: number;
  height: number;
  isAppOpened: boolean;
  isStale: boolean;
  isPoll: boolean;
  handleSetIsPoll: (isPoll: boolean) => void;
  isFirstStale: boolean;
  handleUserReActivity: () => void;
  selectedOption: any[];
  handleSetSelectedOption: (option: any) => void;
}

export const viewportContext = React.createContext<IViewportContext>({
  width: 0,
  height: 0,
  isAppOpened: false,
  isStale: false,
  isPoll: false,
  handleSetIsPoll: () => {},
  isFirstStale: false,
  handleUserReActivity: () => {},
  selectedOption: [],
  handleSetSelectedOption: () => {},
});

export const ViewportProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [width, setWidth] = React.useState(window.innerWidth);
  const [height, setHeight] = React.useState(window.innerHeight);
  const [isAppOpened, setIsAppOpened] = React.useState(false);
  const [isStale, setIsStale] = React.useState(false);
  const [isPoll, setIsVote] = React.useState(false);
  const [lastActivity, setLastActivity] = React.useState(Date.now());
  const [isFirstStale, setIsFirstStale] = React.useState(false);
  const [selectedOption, setSelectedOption] = React.useState<any[]>([]);
  const handleUserActivity = () => {
    if (!isStale) setLastActivity(Date.now());
  };
  const handleUserReActivity = useCallback(() => {
    setLastActivity(Date.now());
    setIsStale(false);
  }, []);
  useEffect(() => {
    const handleMessage = (event: any) => {
      // Ensure the message is from the expected source if needed
      // if (event.origin !== 'http://your-origin') return;
      if (event.data && typeof event.data.isProticoFrameOpen === "boolean") {
        // Handle the message here, for example:
        if (event.data.isProticoFrameOpen) {
          // Do something when the frame is open
          setLastActivity(Date.now());
          setIsStale(false);
        } else {
          // Do something when the frame is closed
        }
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);
  const handleWindowResize = () => {
    setWidth(window.innerWidth);
    setHeight(window.innerHeight);
    if (window.innerWidth > 0 && window.innerHeight > 0) {
      // user open app, start to send request to server
      setIsAppOpened(true);
    }
  };
  const handleSetIsPoll = (isPoll: boolean) => {
    setIsVote(isPoll);
  };
  const handleSetSelectedOption = (option: any) => {
    setSelectedOption(option);
  };

  React.useEffect(() => {
    window.addEventListener("resize", handleWindowResize);
    handleWindowResize();
    return () => {
      window.removeEventListener("resize", handleWindowResize);
    };
  }, []);

  React.useEffect(() => {
    window.addEventListener("mousemove", handleUserActivity);
    window.addEventListener("keydown", handleUserActivity);

    const checkStale = () => {
      const currentTime = Date.now();
      const timeElapsed = currentTime - lastActivity;
      const staleThreshold = 3 * 60 * 1000; // 3 minutes

      if (timeElapsed > staleThreshold) {
        setIsStale(true);
        setIsFirstStale(true);
      }
    };

    const intervalId = setInterval(checkStale, 60000); // Check every minute

    return () => {
      window.removeEventListener("mousemove", handleUserActivity);
      window.removeEventListener("keydown", handleUserActivity);
      clearInterval(intervalId);
    };
  }, [lastActivity]);

  return (
    <viewportContext.Provider
      value={{
        width,
        height,
        isAppOpened,
        isStale,
        isPoll,
        handleSetIsPoll,
        isFirstStale,
        handleUserReActivity,
        selectedOption,
        handleSetSelectedOption,
      }}
    >
      {children}
    </viewportContext.Provider>
  );
};
